import styled, { css } from 'styled-components';
import {
  colors,
  easingTokens,
  r4,
  s16,
  s4,
  s8,
  shadowM,
  space,
  timeFastMs,
  transitionTokens,
} from '@ardoq/design-tokens';
import { caption, header5, text2, text2Bold } from '@ardoq/typography';
import { CheckMarkIcon, ICON_SELECTOR, Icon, IconSize } from '@ardoq/icons';
import {
  Align,
  DropdownButtonOptionType,
  DropdownItemColorMap,
  DropdownSize,
} from './types';
import { FIELD_SELECTOR } from '@ardoq/forms';
import { composeCssStatesColorBlock } from './utils';
import { Color } from '@ardoq/color-helpers';
import { FlexBox, Stack } from '@ardoq/layout';
import { aiSubtleGradientCSS } from '@ardoq/style-helpers';

const isAutomatedTestMode = process.env.NODE_ENV === 'test';

type DropdownMenuWrapperProps = {
  $dropdownSize: DropdownSize;
  $minDropdownSize?: DropdownSize;
  $maxDropdownSize?: DropdownSize;
  $isVisible?: boolean;
  $hasFooterContent?: boolean;
  $isHighlighted?: boolean;
  $maxDropdownHeight?: number;
};

const getDropdownWidth = (dropdownSize: DropdownSize) => {
  if (dropdownSize === DropdownSize.L) return '560px';
  if (dropdownSize === DropdownSize.M) return '448px';
  if (dropdownSize === DropdownSize.S) return '224px';
  if (dropdownSize === DropdownSize.FULL_WIDTH) return '100%';
  return 'auto';
};

export const OptionDescription = styled.div<{
  $shouldTruncate?: boolean;
  $isDisabled?: boolean;
}>`
  ${caption};
  color: ${colors.textSubtle};
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${colors.textDisabled};
    `};
  ${({ $shouldTruncate }) => $shouldTruncate && threeLineEllipsisCSS}
`;

export const OptionLabel = styled.div<{
  $shouldTruncate?: boolean;
  $isHighlighted?: boolean;
  $maxTextLines?: number;
  $hideLabel?: boolean;
}>`
  ${text2};
  color: ${props =>
    props.$isHighlighted ? colors.textOnInvertedStrong : 'inherit'};
  ${({ $shouldTruncate }) => $shouldTruncate && twoLineEllipsisCSS}
  ${({ $shouldTruncate, $maxTextLines = 2 }) =>
    $shouldTruncate &&
    css`
      -webkit-line-clamp: ${$maxTextLines};
    `}
    ${({ $hideLabel }) =>
    $hideLabel &&
    css`
      display: none;
    `}
`;

const twoLineEllipsisCSS = css`
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow-wrap: anywhere;
`;

const threeLineEllipsisCSS = css`
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  overflow-wrap: anywhere;
`;

const menuOptionActiveCSS = css`
  background-color: ${colors.surfaceHover};
`;

const menuOptionAiBaseCSS = css`
  position: relative;
  isolation: isolate;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 6px;
    background: ${aiSubtleGradientCSS()};
    opacity: 0;
    transition: opacity ${transitionTokens.medium} ${easingTokens.cubicOut};
    pointer-events: none;
    z-index: -1;
  }
`;

const menuOptionAiActiveCSS = css`
  &::before {
    opacity: 1;
  }
`;

const getMenuOptionActiveCSS = (isAi?: boolean) =>
  isAi ? menuOptionAiActiveCSS : menuOptionActiveCSS;

const menuOptionSelectedCSS = css`
  &&& {
    background-color: ${colors.surfaceActive};
  }
`;

const menuOptionHighlightedCSS = css`
  background-color: ${colors.blue600};
  &:hover {
    background-color: ${colors.blue700};
  }
`;

const DEFAULT_COLOR_MAP: DropdownItemColorMap = {
  default: {},
  hover: {},
};

const DANGER_COLOR_MAP: DropdownItemColorMap = {
  default: {
    color: colors.textError,
    iconColor: colors.iconError,
  },
  hover: {
    backgroundColor: colors.surfaceError,
  },
};

const DROPDOWN_BUTTON_TYPE_COLOR_MAP: Record<
  DropdownButtonOptionType,
  DropdownItemColorMap
> = {
  [DropdownButtonOptionType.DEFAULT]: DEFAULT_COLOR_MAP,
  [DropdownButtonOptionType.DANGER]: DANGER_COLOR_MAP,
};

export const MenuOptionWrapper = styled.div<{
  $hasLeftIcon?: boolean;
  $isDisabled?: boolean;
  $isFocused?: boolean;
  $isHovered?: boolean;
  $isSelected?: boolean;
  $isClickable?: boolean;
  $isHighlighted?: boolean;
  $isAi?: boolean;
  $buttonType?: DropdownButtonOptionType;
  $colorMap?: DropdownItemColorMap;
}>`
  display: flex;
  padding: ${space.xsmall} ${space.small};
  min-height: 34px;
  justify-content: center;
  align-items: center;
  gap: ${s8};
  overflow-wrap: anywhere;
  color: ${colors.textDefault};
  background-color: ${colors.surfaceDefault};
  border-radius: 6px;
  transition: all ${transitionTokens.medium} ${easingTokens.cubicOut};

  ${OptionDescription} {
    margin-top: ${s4};
  }

  ${({ $isAi }) => $isAi && menuOptionAiBaseCSS}

  /* if isHovered is provided (true | false) then we don't
     use the CSS :hover - this feature is used on keyboard
     navigation menus (like Select) */
  ${({ $isHovered, $isClickable, $colorMap, $isAi }) => {
    // Skip default hover styles when any custom background color is defined
    const hasCustomBackground =
      $colorMap?.default?.backgroundColor || $colorMap?.hover?.backgroundColor;
    const activeCSS = getMenuOptionActiveCSS($isAi);

    return (
      ($isClickable &&
        $isHovered === undefined &&
        css`
          cursor: pointer;
          ${!hasCustomBackground &&
          css`
            &:hover {
              ${activeCSS};
            }
          `}
        `) ||
      ($isHovered === true &&
        css`
          cursor: pointer;
          ${!hasCustomBackground && activeCSS}
        `)
    );
  }}

  ${({ $isSelected }) => $isSelected && menuOptionSelectedCSS}

  ${({ $isFocused, $isAi }) => $isFocused && getMenuOptionActiveCSS($isAi)}

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      color: ${colors.textDisabled};
      /* We have cases where we use a custom img or multicolored svgs in
       * we highlight them as disabled add a opacity, but if the svg is
       * an ardoq icon we revert the opacity and set icon disabled color instead. */
      & > img,
      & > svg {
        opacity: 0.3;
      }
      ${ICON_SELECTOR} {
        color: ${colors.iconActionDisabled};
        opacity: inherit;
      }
    `}

  ${({ $isHighlighted }) => $isHighlighted && menuOptionHighlightedCSS}

  ${({ $buttonType }) =>
    $buttonType &&
    composeCssStatesColorBlock(DROPDOWN_BUTTON_TYPE_COLOR_MAP[$buttonType])}

  ${({ $colorMap }) => $colorMap && composeCssStatesColorBlock($colorMap)}
`;

export const MENU_OPTION_WRAPPER_SELECTOR = MenuOptionWrapper.toString();

export const MenuOptionInnerWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const MenuOptionOuterWrapper = styled.div<{
  $isHovered?: boolean;
  $isClickable?: boolean;
  $isFocused?: boolean;
  $isAi?: boolean;
  $colorMap?: DropdownItemColorMap;
}>`
  flex: 1;
  position: relative;
  /** The following is a workaround to show the MenuOptionWrapper active state when hovering
      or clicking on the outer wrapper */
  padding: 0 ${space.small};
  margin: 0 -${space.small};

  ${({ $isHovered, $isClickable, $colorMap, $isAi }) => {
    // Skip default hover styles when any custom background color is defined
    const hasCustomBackground =
      $colorMap?.default?.backgroundColor || $colorMap?.hover?.backgroundColor;
    const activeCSS = getMenuOptionActiveCSS($isAi);

    return (
      $isClickable &&
      $isHovered === undefined &&
      css`
        cursor: pointer;
        ${!hasCustomBackground &&
        css`
          &:hover > ${MENU_OPTION_WRAPPER_SELECTOR} {
            ${activeCSS};
          }
        `}
      `
    );
  }}
  ${({ $isFocused, $isAi }) =>
    $isFocused &&
    css`
      cursor: pointer;
      ${MENU_OPTION_WRAPPER_SELECTOR} {
        ${getMenuOptionActiveCSS($isAi)}
      }
    `}
`;

export const MenuOptionOuterWrapperWithContent = styled.div`
  position: relative;
`;

export const ActiveContentWrapper = styled.div`
  position: absolute;
  right: ${s16};
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
`;

const MENU_OPTION_SELECTOR = MenuOptionOuterWrapper.toString();

export const MenuOptionBody = styled.div<{
  $align?: Align;
  $hasRightIcon?: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${s8};

  ${ICON_SELECTOR} {
    flex: 0 ${IconSize.MEDIUM}px;
  }

  ${OptionLabel} {
    flex: ${({ $hasRightIcon }) => ($hasRightIcon ? '0 1 auto' : '1 1 auto')};
  }

  ${({ $align }) =>
    ($align === 'center' &&
      css`
        justify-content: center;
        ${OptionLabel}, ${ICON_SELECTOR} {
          flex: 0 auto;
        }
      `) ||
    ($align === 'right' &&
      css`
        justify-content: flex-end;
        ${OptionLabel}, ${ICON_SELECTOR} {
          flex: 0 auto;
        }
      `)}
`;
MenuOptionBody.displayName = 'MenuOptionBody';

export const MENU_OPTION_BODY_SELECTOR = MenuOptionBody.toString();

export const DropdownMenuSectionSplit = styled.div`
  border-bottom: 1px solid ${colors.borderSubtle};
  height: 1px;
  margin: ${space.small} -${space.small};
`;
DropdownMenuSectionSplit.displayName = 'DropdownMenuSectionSplit';

const wrappersSpacingCSS = css`
  & > ${OptionDescription} {
    margin: ${s4} ${space.small} 0;
  }

  & > ${MENU_OPTION_SELECTOR} {
    padding-top: ${space.xsmall};
    &:first-child {
      padding-top: 0;
    }
  }

  & > ${DropdownMenuSectionSplit} + ${MENU_OPTION_SELECTOR} {
    padding-top: 0;
  }
`;

// Usually this element is not needed at all, but structure of
// modal dropdown requires an extra element, so I have added this
// additional element
export const DropdownMenuSectionWrapper = styled.div`
  background-color: ${colors.surfaceDefault};
  color: ${colors.textDefault};
  ${wrappersSpacingCSS};
  &:last-child {
    ${DropdownMenuSectionSplit}:last-child {
      display: none;
    }
  }
`;
DropdownMenuSectionWrapper.displayName = 'DropdownMenuSectionWrapper';

export const DropdownMenuBodyWrapper = styled.div<{
  $noScrollOnWrapper?: boolean;
  $useDisplayFlexForMainContent?: boolean;
}>`
  ${wrappersSpacingCSS};
  padding: ${space.small};
  overflow: ${({ $noScrollOnWrapper }) =>
    $noScrollOnWrapper ? 'hidden' : 'auto'};

  & > ${DropdownMenuSectionSplit}:last-child {
    display: none;
  }
  ${({ $useDisplayFlexForMainContent }) =>
    $useDisplayFlexForMainContent
      ? css`
          display: flex;
          flex-direction: column;
        `
      : ''};
`;

export const dropdownMenuWrapperCSS = css<DropdownMenuWrapperProps>`
  border-radius: ${r4};
  ${({
    $dropdownSize,
    $minDropdownSize,
    $maxDropdownSize,
    $maxDropdownHeight,
  }) => `
    width: ${getDropdownWidth($dropdownSize)};
    ${
      $minDropdownSize
        ? `min-width: ${getDropdownWidth($minDropdownSize)};`
        : ''
    }
    ${
      $maxDropdownSize
        ? `max-width: ${getDropdownWidth($maxDropdownSize)};`
        : ''
    }
    max-height: ${$maxDropdownHeight ?? 300}px;
  `};
  overflow-y: auto;
  box-shadow: ${shadowM};
  background-color: ${colors.surfaceDefault};
`;

export const DropdownMenuWrapper = styled(FlexBox).attrs({
  flexDirection: 'column',
})<DropdownMenuWrapperProps>`
  ${dropdownMenuWrapperCSS};
  overflow: hidden;

  /* ANIMATION */
  transition:
    opacity ${timeFastMs},
    transform ${timeFastMs},
    pointer-events ${timeFastMs};
  ${({ $isVisible }) =>
    ({
      undefined: css``,
      true: css`
        opacity: 1;
        pointer-events: auto;
      `,
      false: css`
        opacity: 0;
        transform: translateY(-20px);
        pointer-events: ${isAutomatedTestMode ? 'auto' : 'none'};
      `,
    })[`${$isVisible}`]}
`;

export const DropdownActionStyle = styled(FlexBox).attrs({
  gap: 'medium',
})`
  ${text2Bold};
  cursor: pointer;
  color: ${colors.textModerate};
  &:hover {
    color: ${colors.textDefault};
  }
`;

export const DropdownFooterActionSpace = styled(DropdownActionStyle)`
  flex: 1;
  padding: ${s16} ${s16};
`;

export const DropdownMenuHeaderWrapper = styled(Stack).attrs({
  gap: 'medium',
})`
  &:first-child {
    border-bottom: 1px solid ${colors.borderAction};
  }
  &:last-child {
    border-top: 1px solid ${colors.borderAction};
  }

  ${FIELD_SELECTOR} {
    padding: ${s16};
  }
`;

export const DropdownMenuSectionTitle = styled.div<{
  $shouldTruncate?: boolean;
}>`
  ${header5};
  display: flex;
  align-items: center;
  gap: ${s8};
  color: ${colors.textSubtle};
  padding: 0 ${space.small};
  min-height: 24px;
  &:last-child {
    padding-top: ${s16};
  }
  ${({ $shouldTruncate }) => $shouldTruncate && twoLineEllipsisCSS}
`;

export const OptionCheckIcon = styled(CheckMarkIcon)`
  color: ${colors.iconActionActive};
`;

export const OptionIcon = styled(Icon)<{
  $isHighlighted?: boolean;
  $color?: Color;
}>`
  color: ${({ $isHighlighted, $color }) => {
    if ($color) return $color;
    return $isHighlighted ? colors.iconOnInvertedStrong : colors.icon;
  }};
`;

export const CheckboxWrapper = styled.div`
  height: 20px;
`;
